import React, { Component } from "react";
import { IonModal, IonButton, IonInput } from "@ionic/react";

// Images
import iconClearSearch from "../assets/Button_Cancel@3x.png";
import iconSearch from "../assets/Button_Search_Small@3x.png";
import iconSearchDark from "../assets/Button_Search_Small_Dark@3x.png";
import iconSearchGrey from "../assets/Icon_Search_Grey@3x.png";

// CSS
import "../Shared.css";
import "../../App.css";
import "./GlobalSearchModal.css";

import {
  IFurtherResource,
  IPathway,
  IPathwayBypassNumber,
  IPathwayWhatGoesWhere,
  IRecentSearch,
  IScreenProps,
  ISearchResult,
} from "../Interfaces";
import DataController from "../controllers/DataController";
import RemoteDataController from "../controllers/RemoteDataController";

import SearchResultCell from "./SearchResultCell";
import FilterChip from "./FilterChip";
import MessageBar from "./MessageBar";

import _ from "lodash";

type Props = {
  features: string[];
  screenProps: IScreenProps;
  match: any;
  history: any;
  furtherResourceCellClicked: (furtherResource: IFurtherResource) => void;
};
type State = {
  searchText: string;
  searchModalVisible: boolean;
  recentSearches: IRecentSearch[];
  recentSearchPhrases: string[];
  selectedSearchFilter: string;
  matchingSearchResults: ISearchResult[];
};

class GlobalSearchModal extends Component<Props, State> {
  private searchTextField: React.RefObject<HTMLIonInputElement>;

  constructor(props: Props) {
    super(props);

    this.searchTextField = React.createRef();

    this.state = {
      searchModalVisible: false,
      searchText: "",
      recentSearches: [],
      recentSearchPhrases: [],
      selectedSearchFilter: "All",
      matchingSearchResults: [],
    };
  }

  async componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
    if (prevProps.screenProps.guidelineTextFinishedLoading !== this.props.screenProps.guidelineTextFinishedLoading) {
      this.updateSearchResults();
    }
  }

  showSearch(shouldShow: boolean) {
    this.setState({ searchModalVisible: shouldShow });

    if (shouldShow) {
      this.setState({ searchText: "" }, () => {
        this.updateRecentSearches();
        this.updateSearchResults();
      });

      setTimeout(() => {
        this.searchTextField.current?.setFocus();
      }, 300);
    }
  }

  searchTextChanged(event: any) {
    const value = event.target.value;

    this.setState({ searchText: value }, () => {
      this.updateSearchResults();
    });
  }

  enterButtonClicked(event: any) {
    if (event.key === "Enter") {
    }
  }

  clearSearch() {
    this.searchTextField.current?.setFocus();
    this.setState({ searchText: "" });
  }

  async updateRecentSearches() {
    let recentSearches = await DataController.getRecentSearches();
    let recentSearchPhrases = await DataController.getRecentSearchPhrases();
    this.setState({ recentSearches, recentSearchPhrases });
  }

  async clearRecentSearches() {
    await DataController.clearRecentSearches();
    this.setState({ recentSearches: [] });
  }

  async clearRecentSearchPhrases() {
    await DataController.clearRecentSearchPhrases();
    this.setState({ recentSearchPhrases: [] });
  }

  async updateSearchResults() {
    let matchingSearchResults = [];

    let searchText = this.state.searchText
      .toLowerCase()
      .trimStart()
      .replace(/\(/g, "\\(")
      .replace(/\)/g, "\\)")
      .replace(/\{/g, "\\{")
      .replace(/\}/g, "\\}")
      .replace(/\./g, "\\.");

    if (searchText.length < 2) {
      return;
    }

    let trust = "";
    let trustDisplayName = "";
    if (this.props.screenProps.user) {
      trust = this.props.screenProps.user?.trust;
      if (trust !== "") {
        trustDisplayName = DataController.getTrustDisplayName(trust);
      }
    }

    let searchRegex = "";
    for (let searchWord of searchText.split(" ")) {
      searchRegex += `(?=.*\\b${searchWord})`;
    }
    let wordBeginsWithRegex = new RegExp(searchRegex);
    // let wordBeginsWithRegex = new RegExp("\\b" + searchText);

    let guidelineBookmarkGLIDs = this.props.screenProps.bookmarks
      .filter((bookmark) => bookmark.type === "guideline")
      .map((bookmark) => bookmark.GLID);
    let algorithmBookmarkIDs = this.props.screenProps.bookmarks
      .filter((bookmark) => bookmark.type === "algorithm")
      .map((bookmark) => bookmark.algorithmID);
    let clinicalNoticeBookmarkIDs = this.props.screenProps.bookmarks
      .filter((bookmark) => bookmark.type === "clinical notice")
      .map((bookmark) => bookmark.clinicalNoticeID);
    let pathwayBookmarkIDs = this.props.screenProps.bookmarks
      .filter((bookmark) => bookmark.type === "pathway")
      .map((bookmark) => bookmark.pathwayID);
    let essentialsBookmarkIDs = this.props.screenProps.bookmarks
      .filter((bookmark) => bookmark.type === "essentials")
      .map((bookmark) => bookmark.GLID);

    // guidelines
    for (
      let guidelineSummaryIndex = 0;
      guidelineSummaryIndex < this.props.screenProps.guidelineSummaries.length;
      guidelineSummaryIndex++
    ) {
      let guidelineSummary = this.props.screenProps.guidelineSummaries[guidelineSummaryIndex];

      let guidelineMatches = 0;
      let titleMatch = false;
      let guidelineScore = 0;
      let guidelineMatchSample = null;

      // filter out guidelines
      if (
        this.state.selectedSearchFilter !== "All" &&
        this.state.selectedSearchFilter !== "Guideline" &&
        this.state.selectedSearchFilter !== "Medicine" &&
        this.state.selectedSearchFilter !== trustDisplayName
      ) {
        continue;
      }

      if (this.state.selectedSearchFilter === "Guideline" && DataController.isDrug(guidelineSummary)) {
        continue;
      }

      if (this.state.selectedSearchFilter === trustDisplayName && guidelineSummary.trust == null) {
        continue;
      }

      if (this.state.selectedSearchFilter === "Medicine" && DataController.isDrug(guidelineSummary) === false) {
        continue;
      }

      // match search text
      if (searchText.length > 1) {
        let guidelineText = this.props.screenProps.getGuidelineText(guidelineSummary.GLID);

        if (guidelineSummary.GLID === "G0380") {
          let test = "";
        }

        // check full text for search match
        const result = [];

        if (guidelineText !== null && guidelineText !== undefined) {
          try {
            let match;
            var re = new RegExp(searchText, "gi");
            if (searchText.length > 0) {
              while ((match = re.exec(guidelineText)) != null) {
                result.push(match);
                if (guidelineMatchSample == null) {
                  let startingIndex = match.index - 20;
                  if (startingIndex < 0) {
                    startingIndex = 0;
                  }

                  guidelineMatchSample = guidelineText.substr(startingIndex, 80);
                }
              }
            }
          } catch (error) {
            console.log("error with search: " + error);
          }
        }

        if (result.length > 0) {
          guidelineMatches += result.length;
          guidelineScore += result.length;
        }

        if (guidelineSummary.IndexWords) {
          let indexedWords = guidelineSummary.IndexWords.match(/(?:[^\s']+|'[^']*')+/g);
          if (indexedWords) {
            for (let wordIndex = 0; wordIndex < indexedWords.length; wordIndex++) {
              let word = indexedWords[wordIndex];
              if (wordBeginsWithRegex.test(word.toLowerCase())) {
                guidelineScore += 500;
                break;
              }
            }
          }
        }

        let guidelineTitle = guidelineSummary.Title;
        let guidelineSubtitle = guidelineSummary.SubTitle;
        let guidelineSearchString = "JRCALC " + guidelineTitle;

        if (guidelineSummary.GLID === "D0040") {
          let test = "";
        }

        if (searchText !== "" && guidelineSubtitle != null && guidelineSubtitle.toLowerCase().startsWith(searchText)) {
          guidelineMatches++;
          titleMatch = true;
          guidelineScore += 2500;
        } else if (searchText !== "" && guidelineTitle.toLowerCase().includes(searchText)) {
          guidelineMatches++;
          titleMatch = true;

          if (guidelineTitle.toLowerCase().startsWith(searchText)) {
            guidelineScore += 2300;
          } else if (wordBeginsWithRegex.test(guidelineTitle.toLowerCase())) {
            guidelineScore += 2000;
          } else {
            guidelineScore += 1000;
          }
        } else if (guidelineSummary.GLID.toLowerCase().includes(searchText)) {
          guidelineMatches++;
          guidelineScore += 2300;
        } else if (searchText !== "" && guidelineSearchString.toLowerCase().includes(searchText)) {
          guidelineScore += 100;
        } else if (
          guidelineSummary.GuidelineType != null &&
          guidelineSummary.GuidelineType.toLowerCase().includes(searchText)
        ) {
          guidelineScore += 100;
        } else if (DataController.isPGD(guidelineSummary) && searchText.includes("pgd")) {
          guidelineScore += 100;
        } else if (DataController.isPMA(guidelineSummary) && searchText.includes("pma")) {
          guidelineScore += 100;
        }

        if (DataController.isDrug(guidelineSummary) && guidelineScore > 0) {
          guidelineScore += 10; // prefer drugs over guidelines if similar score
        }

        if (guidelineBookmarkGLIDs.includes(guidelineSummary.GLID) && guidelineScore > 0) {
          guidelineScore += 100;
        }
      }

      if (guidelineMatches > 0 || guidelineScore > 0) {
        let searchResult: ISearchResult = {
          type: "guideline",
          guidelineSummary: guidelineSummary,
          algorithm: null,
          clinicalNotice: null,
          titleMatch: titleMatch,
          matches: guidelineMatches,
          score: guidelineScore,
          sample: guidelineMatchSample,
        };

        matchingSearchResults.push(searchResult);
      }
    }

    // algorithms
    for (let algorithmIndex = 0; algorithmIndex < this.props.screenProps.algorithms.length; algorithmIndex++) {
      let algorithm = this.props.screenProps.algorithms[algorithmIndex];

      if (this.state.selectedSearchFilter !== "All" && this.state.selectedSearchFilter !== "Algorithm") {
        continue;
      }

      let algorithmMatches = 0;
      let algorithmScore = 0;
      let algorithmMatchSample = null;
      let titleMatch = false;

      // match search text
      if (searchText.length > 1) {
        if (searchText !== "" && algorithm.name.toLowerCase().includes(searchText)) {
          algorithmMatches++;
          titleMatch = true;

          if (algorithm.name.toLowerCase().startsWith(searchText)) {
            algorithmScore += 2300;
          } else if (wordBeginsWithRegex.test(algorithm.name.toLowerCase())) {
            algorithmScore += 2000;
          } else {
            algorithmScore += 1000;
          }
        } else if (algorithm.GLID != null && algorithm.GLID.toLowerCase().includes(searchText)) {
          algorithmMatches++;
          algorithmScore += 2300;
        }

        if (algorithmBookmarkIDs.includes(algorithm.id) && algorithmScore > 0) {
          algorithmScore += 100;
        }
      }

      if (algorithmMatches > 0 || algorithmScore > 0) {
        let searchResult: ISearchResult = {
          type: "algorithm",
          guidelineSummary: null,
          algorithm: algorithm,
          clinicalNotice: null,
          titleMatch: titleMatch,
          matches: algorithmMatches,
          score: algorithmScore,
          sample: algorithmMatchSample,
        };

        matchingSearchResults.push(searchResult);
      }
    }

    // clinical notices
    for (
      let clinicalNoticeIndex = 0;
      clinicalNoticeIndex < this.props.screenProps.clinicalNotices.length;
      clinicalNoticeIndex++
    ) {
      let clinicalNotice = this.props.screenProps.clinicalNotices[clinicalNoticeIndex];

      if (this.state.selectedSearchFilter !== "All" && this.state.selectedSearchFilter !== "Clinical Notice") {
        continue;
      }

      let clinicalNoticeMatches = 0;
      let clinicalNoticeScore = 0;
      let clinicalNoticeMatchSample = null;
      let titleMatch = false;

      // match search text
      if (this.state.searchText.length > 1) {
        if (searchText !== "" && clinicalNotice.Title.toLowerCase().includes(searchText)) {
          clinicalNoticeMatches++;
          titleMatch = true;

          if (clinicalNotice.Title.toLowerCase().startsWith(searchText)) {
            clinicalNoticeScore += 2300;
          } else if (wordBeginsWithRegex.test(clinicalNotice.Title.toLowerCase())) {
            clinicalNoticeScore += 2000;
          } else {
            clinicalNoticeScore += 1000;
          }
        }

        // check full text for search match
        const result = [];

        let clinicalNoticeText = this.props.screenProps.getClinicalNoticeText(clinicalNotice.ID);

        if (clinicalNoticeText != null && clinicalNoticeText !== "") {
          try {
            let match;
            var re = new RegExp(searchText, "gi");
            if (searchText.length > 0) {
              while ((match = re.exec(clinicalNoticeText)) != null) {
                result.push(match);
                if (clinicalNoticeMatchSample == null) {
                  let startingIndex = match.index - 20;
                  if (startingIndex < 0) {
                    startingIndex = 0;
                  }

                  clinicalNoticeMatchSample = clinicalNoticeText.substr(startingIndex, 80);
                }
              }
            }
          } catch (error) {
            console.log("error with search: " + error);
          }
        }

        if (result.length > 0) {
          clinicalNoticeMatches += result.length;
          clinicalNoticeScore += result.length;
        }

        // match keywords
        if (clinicalNotice.Keywords && searchText.length > 1) {
          let keywords = clinicalNotice.Keywords.split(",");
          if (keywords) {
            for (let wordIndex = 0; wordIndex < keywords.length; wordIndex++) {
              let word = keywords[wordIndex];
              if (word.toLowerCase().includes(searchText)) {
                clinicalNoticeScore += 500;
                break;
              }
            }
          }
        }

        if (clinicalNoticeBookmarkIDs.includes(clinicalNotice.ID) && clinicalNoticeScore > 0) {
          clinicalNoticeScore += 100;
        }
      }

      if (clinicalNoticeMatches > 0 || clinicalNoticeScore > 0) {
        let searchResult: ISearchResult = {
          type: "clinical notice",
          guidelineSummary: null,
          algorithm: null,
          clinicalNotice: clinicalNotice,
          titleMatch: titleMatch,
          matches: clinicalNoticeMatches,
          score: clinicalNoticeScore,
          sample: clinicalNoticeMatchSample,
        };

        matchingSearchResults.push(searchResult);
      }
    }

    // pathways
    let allPathways: (IPathway | IPathwayBypassNumber | IPathwayWhatGoesWhere)[] = [];

    if (this.props.screenProps.pathwayData) {
      if (this.props.screenProps.pathwayData.Pathways) {
        allPathways = allPathways.concat(this.props.screenProps.pathwayData.Pathways);
      }
      if (this.props.screenProps.pathwayData.Bypassnumbers) {
        allPathways = allPathways.concat(this.props.screenProps.pathwayData.Bypassnumbers);
      }
      if (this.props.screenProps.pathwayData.Whatgoeswhere) {
        allPathways = allPathways.concat(this.props.screenProps.pathwayData.Whatgoeswhere);
      }
    }

    for (let pathwayIndex = 0; pathwayIndex < allPathways.length; pathwayIndex++) {
      let pathway = allPathways[pathwayIndex];

      let pathwayName = DataController.getPathwayName(pathway);

      if (this.state.selectedSearchFilter !== "All" && this.state.selectedSearchFilter !== "Pathway") {
        continue;
      }

      let pathwayMatches = 0;
      let pathwayScore = 0;
      let titleMatch = false;
      let pathwayMatchSample = null;

      // match search text
      if (this.state.searchText.length > 1) {
        if (searchText !== "") {
          if (pathwayName.toLowerCase().startsWith(searchText)) {
            pathwayScore += 20;
            pathwayMatches++;
            titleMatch = true;
          } else if (wordBeginsWithRegex.test(pathwayName.toLowerCase())) {
            pathwayScore += 15;
            pathwayMatches++;
            titleMatch = true;
          } else if (pathwayName.toLowerCase().includes(searchText)) {
            pathwayScore += 10;
            pathwayMatches++;
            titleMatch = true;
          }
        }

        if (DataController.isPathway(pathway)) {
          if (pathway.fields["Pathway ID"] && wordBeginsWithRegex.test(pathway.fields["Pathway ID"].toLowerCase())) {
            pathwayScore += 15;
          }

          if (pathway.fields.Area && pathway.fields.Area.length > 0) {
            if (pathway.fields.Area.find((area) => area.toLowerCase().includes(searchText))) {
              pathwayScore += 5;
            }
          }
        }

        // match keywords
        if (pathway.fields["Search Terms"]) {
          let searchTerms = pathway.fields["Search Terms"];
          if (searchTerms) {
            if (wordBeginsWithRegex.test(searchTerms.join(" ").toLowerCase())) {
              pathwayScore += 5;
            }
          }
        }

        if (pathwayBookmarkIDs.includes(pathway.id) && pathwayScore > 0) {
          pathwayScore += 1;
        }

        // prioritise pathways that match user's region - but only if already matching
        if (pathwayScore > 0 && DataController.isPathway(pathway)) {
          if (this.props.screenProps.user?.regions) {
            let userRegions = this.props.screenProps.user.regions;
            let pathwayRegions = pathway.fields["User regions"];
            if (pathwayRegions) {
              for (let region of pathwayRegions) {
                if (userRegions.includes(region)) {
                  pathwayScore += 20;
                }
              }
            }
          }
        }
      }

      if (pathwayMatches > 0 || pathwayScore > 0) {
        let searchResult: ISearchResult = {
          type: "pathway",
          guidelineSummary: null,
          algorithm: null,
          clinicalNotice: null,
          pathway: pathway,
          titleMatch: titleMatch,
          matches: pathwayMatches,
          score: pathwayScore,
          sample: pathwayMatchSample,
        };

        matchingSearchResults.push(searchResult);
      }
    }

    // essentials
    if (this.props.screenProps.app === "CFR") {
      for (
        let essentialSectionIndex = 0;
        essentialSectionIndex < this.props.screenProps.essentialSections.length;
        essentialSectionIndex++
      ) {
        let essentialSection = this.props.screenProps.essentialSections[essentialSectionIndex];

        if (this.state.selectedSearchFilter !== "All" && this.state.selectedSearchFilter !== "Essentials") {
          continue;
        }

        let essentialSectionMatches = 0;
        let essentialSectionScore = 0;
        let essentialSectionMatchSample = null;
        let titleMatch = false;

        // match search text
        if (searchText.length > 1) {
          if (searchText !== "" && essentialSection.Name.toLowerCase().includes(searchText)) {
            essentialSectionMatches++;
            titleMatch = true;

            if (essentialSection.Name.toLowerCase().startsWith(searchText)) {
              essentialSectionScore += 2300;
            } else if (wordBeginsWithRegex.test(essentialSection.Name.toLowerCase())) {
              essentialSectionScore += 2000;
            } else {
              essentialSectionScore += 1000;
            }
          }

          // match essentials text
          let essentialSectionText = this.props.screenProps.getEssentialsText(essentialSection.id);

          const result = [];

          if (essentialSectionText !== null && essentialSectionText !== undefined) {
            try {
              let match;
              var re = new RegExp(searchText, "gi");
              if (searchText.length > 0) {
                while ((match = re.exec(essentialSectionText)) != null) {
                  result.push(match);
                  if (essentialSectionMatchSample == null) {
                    let startingIndex = match.index - 20;
                    if (startingIndex < 0) {
                      startingIndex = 0;
                    }

                    essentialSectionMatchSample = essentialSectionText.substr(startingIndex, 80);
                  }
                }

                if (result.length > 0) {
                  essentialSectionMatches += result.length;
                  essentialSectionScore += result.length;
                }
              }
            } catch (error) {
              console.log("error with search: " + error);
            }
          }

          if (essentialsBookmarkIDs.includes(essentialSection.id) && essentialSectionScore > 0) {
            essentialSectionScore += 100;
          }
        }

        if (essentialSectionMatches > 0 || essentialSectionScore > 0) {
          let searchResult: ISearchResult = {
            type: "essentials",
            guidelineSummary: null,
            algorithm: null,
            clinicalNotice: null,
            essentialSection: essentialSection,
            titleMatch: titleMatch,
            matches: essentialSectionMatches,
            score: essentialSectionScore,
            sample: essentialSectionMatchSample,
          };

          matchingSearchResults.push(searchResult);
        }
      }
    }

    // further resources
    for (
      let furtherResourceIndex = 0;
      furtherResourceIndex < this.props.screenProps.furtherResources.length;
      furtherResourceIndex++
    ) {
      let furtherResource = this.props.screenProps.furtherResources[furtherResourceIndex];

      if (this.state.selectedSearchFilter !== "All" && this.state.selectedSearchFilter !== "Further Resource") {
        continue;
      }

      let furtherResourceMatches = 0;
      let furtherResourceScore = 0;
      let furtherResourceMatchSample = null;
      let titleMatch = false;

      // match search text
      if (this.state.searchText.length > 1) {
        if (searchText !== "" && furtherResource.name.toLowerCase().includes(searchText)) {
          furtherResourceMatches++;
          titleMatch = true;

          if (furtherResource.name.toLowerCase().startsWith(searchText)) {
            furtherResourceScore += 2100;
          } else if (wordBeginsWithRegex.test(furtherResource.name.toLowerCase())) {
            furtherResourceScore += 1900;
          } else {
            furtherResourceScore += 800;
          }
        }
      }

      if (furtherResourceMatches > 0 || furtherResourceScore > 0) {
        let searchResult: ISearchResult = {
          type: "further resource",
          guidelineSummary: null,
          algorithm: null,
          clinicalNotice: null,
          furtherResource: furtherResource,
          titleMatch: titleMatch,
          matches: furtherResourceMatches,
          score: furtherResourceScore,
          sample: furtherResourceMatchSample,
        };

        matchingSearchResults.push(searchResult);
      }
    }

    matchingSearchResults = _.orderBy(matchingSearchResults, "score", "desc");

    this.setState({ matchingSearchResults });
  }

  searchResultCellClicked(searchResult: ISearchResult, recentViewedResult: boolean) {
    let fromType = "Dashboard Search";
    if (recentViewedResult) {
      fromType = "Dashboard Search Recent";
    }

    if (searchResult.type === "guideline") {
      if (searchResult.guidelineSummary != null) {
        this.props.screenProps.navigateToGuideline(
          searchResult.guidelineSummary.GLID,
          this.props.match.params.tab,
          this.props.history,
          { searchTerm: this.state.searchText },
          fromType
        );
      }
    } else if (searchResult.type === "algorithm") {
      if (searchResult.algorithm != null) {
        this.props.screenProps.navigateToAlgorithm(
          searchResult.algorithm.id,
          this.props.match.params.tab,
          this.props.history,
          fromType
        );
      }
    } else if (searchResult.type === "clinical notice") {
      if (searchResult.clinicalNotice != null) {
        this.props.screenProps.navigateToClinicalNotice(
          searchResult.clinicalNotice.ID,
          this.props.match.params.tab,
          this.props.history,
          { searchTerm: this.state.searchText },
          fromType
        );
      }
    } else if (searchResult.type === "pathway") {
      if (searchResult.pathway != null) {
        this.props.screenProps.navigateToPathway(
          searchResult.pathway,
          this.props.match.params.tab,
          this.props.history,
          fromType
        );
      }
    } else if (searchResult.type === "essentials") {
      if (searchResult.essentialSection != null) {
        this.props.screenProps.navigateToEssentialSection(
          searchResult.essentialSection,
          this.props.match.params.tab,
          this.props.history,
          fromType
        );
      }
    } else if (searchResult.type === "further resource") {
      if (searchResult.furtherResource != null) {
        this.props.furtherResourceCellClicked(searchResult.furtherResource);
      }
    }

    DataController.saveRecentSearch(searchResult);

    if (this.state.searchText.length > 1) {
      DataController.saveRecentSearchPhrase(this.state.searchText);
    }

    if (recentViewedResult) {
      RemoteDataController.logEvent("search_recently_viewed_clicked", {
        glid: searchResult.guidelineSummary
          ? this.props.screenProps.getGuidelineTitleForLogging(searchResult.guidelineSummary.GLID)
          : "none",
        algorithmID: searchResult.algorithm ? searchResult.algorithm.id : "none",
        clinicalNoticeID: searchResult.clinicalNotice ? searchResult.clinicalNotice.ID : "none",
        pathwayID: searchResult.pathway ? searchResult.pathway.id : "none",
        essentialSectionID: searchResult.essentialSection ? searchResult.essentialSection.id : "none",
        furtherResourceName: searchResult.furtherResource ? searchResult.furtherResource.name : "none",
      });
    } else {
      if (this.state.searchText.length > 1) {
        RemoteDataController.logEvent("search", {
          search_term: this.state.searchText,
        });
      }
    }

    this.showSearch(false);
  }

  recentSearchPhraseClicked(phrase: string) {
    this.setState({ searchText: phrase }, () => {
      this.updateSearchResults();
    });
  }

  searchFilterChipClicked(value: string) {
    this.setState({ selectedSearchFilter: value }, () => {
      this.updateSearchResults();
    });
  }

  render() {
    let trust = "";
    if (this.props.screenProps.user) {
      trust = this.props.screenProps.user?.trust;
    }

    let trustDisplayName = "";
    if (trust !== "") {
      trustDisplayName = DataController.getTrustDisplayName(trust);
    }

    let allPathways: (IPathway | IPathwayBypassNumber | IPathwayWhatGoesWhere)[] = [];

    if (this.props.screenProps.pathwayData) {
      if (this.props.screenProps.pathwayData.Pathways) {
        allPathways = allPathways.concat(this.props.screenProps.pathwayData.Pathways);
      }
      if (this.props.screenProps.pathwayData.Bypassnumbers) {
        allPathways = allPathways.concat(this.props.screenProps.pathwayData.Bypassnumbers);
      }
      if (this.props.screenProps.pathwayData.Whatgoeswhere) {
        allPathways = allPathways.concat(this.props.screenProps.pathwayData.Whatgoeswhere);
      }
    }

    let guidelineBookmarkGLIDs = this.props.screenProps.bookmarks
      .filter((bookmark) => bookmark.type === "guideline")
      .map((bookmark) => bookmark.GLID);
    let algorithmBookmarkIDs = this.props.screenProps.bookmarks
      .filter((bookmark) => bookmark.type === "algorithm")
      .map((bookmark) => bookmark.algorithmID);
    let clinicalNoticeBookmarkIDs = this.props.screenProps.bookmarks
      .filter((bookmark) => bookmark.type === "clinical notice")
      .map((bookmark) => bookmark.clinicalNoticeID);
    let pathwayBookmarkIDs = this.props.screenProps.bookmarks
      .filter((bookmark) => bookmark.type === "pathway")
      .map((bookmark) => bookmark.pathwayID);
    let essentialsBookmarkIDs = this.props.screenProps.bookmarks
      .filter((bookmark) => bookmark.type === "essentials")
      .map((bookmark) => bookmark.GLID);

    // render search results
    let filterInfoText = "";
    let resultsCount = this.state.matchingSearchResults.length;

    filterInfoText += resultsCount + " results";

    filterInfoText += ` for '${this.state.searchText}'`;

    if (filterInfoText !== "" && this.props.screenProps.guidelineTextFinishedLoading === false) {
      filterInfoText += ". Full text search is still loading";
    }

    if (this.state.searchText.length < 2) {
      filterInfoText = "";
    }

    let searchResultCells = [];
    let searchResultPhraseCells = [];

    let showingRecentSearches = false;

    if (this.state.searchText.length < 2) {
      // show recent searches

      if (this.state.recentSearches.length > 0 || this.state.recentSearchPhrases.length > 0) {
        showingRecentSearches = true;
      }

      for (let recentSearchIndex = 0; recentSearchIndex < this.state.recentSearches.length; recentSearchIndex++) {
        let recentSearch = this.state.recentSearches[recentSearchIndex];

        let searchResult: ISearchResult = {
          type: recentSearch.type,
          guidelineSummary: null,
          algorithm: null,
          clinicalNotice: null,
          pathway: null,
          essentialSection: null,
          furtherResource: null,
          titleMatch: false,
          matches: 0,
          score: 0,
          sample: null,
        };

        let shouldAddSearchResultCell = true;

        if (searchResult.type === "guideline") {
          let guidelineSummary = this.props.screenProps.guidelineSummaries.find(
            (guidelineSummary) => guidelineSummary.GLID === recentSearch.GLID
          );
          if (guidelineSummary != null) {
            searchResult.guidelineSummary = guidelineSummary;
          } else {
            shouldAddSearchResultCell = false;
          }
        } else if (searchResult.type === "algorithm") {
          let algorithm = this.props.screenProps.algorithms.find(
            (algorithm) => algorithm.id === recentSearch.algorithmID
          );
          if (algorithm != null) {
            searchResult.algorithm = algorithm;
          } else {
            shouldAddSearchResultCell = false;
          }
        } else if (searchResult.type === "clinical notice") {
          let clinicalNotice = this.props.screenProps.clinicalNotices.find(
            (clinicalNotice) => clinicalNotice.ID === recentSearch.clinicalNoticeID
          );
          if (clinicalNotice != null) {
            searchResult.clinicalNotice = clinicalNotice;
          } else {
            shouldAddSearchResultCell = false;
          }
        } else if (searchResult.type === "pathway") {
          let pathway = allPathways.find((pathway) => pathway.id === recentSearch.pathwayID);
          if (pathway != null) {
            searchResult.pathway = pathway;
          } else {
            shouldAddSearchResultCell = false;
          }
        } else if (searchResult.type === "essentials") {
          let essentialSection = this.props.screenProps.essentialSections.find(
            (essentialSection) => essentialSection.id === recentSearch.essentialSectionID
          );
          if (essentialSection != null) {
            searchResult.essentialSection = essentialSection;
          } else {
            shouldAddSearchResultCell = false;
          }
        } else if (searchResult.type === "further resource") {
          let furtherResource = this.props.screenProps.furtherResources.find(
            (furtherResource) => furtherResource.resourceID === recentSearch.furtherResourceID
          );
          if (furtherResource != null) {
            searchResult.furtherResource = furtherResource;
          } else {
            shouldAddSearchResultCell = false;
          }
        }

        if (shouldAddSearchResultCell) {
          let bookmarked = false;
          if (searchResult.clinicalNotice && clinicalNoticeBookmarkIDs.includes(searchResult.clinicalNotice.ID)) {
            bookmarked = true;
          } else if (searchResult.pathway && pathwayBookmarkIDs.includes(searchResult.pathway.id)) {
            bookmarked = true;
          } else if (searchResult.algorithm && algorithmBookmarkIDs.includes(searchResult.algorithm.id)) {
            bookmarked = true;
          } else if (
            searchResult.guidelineSummary &&
            guidelineBookmarkGLIDs.includes(searchResult.guidelineSummary.GLID)
          ) {
            bookmarked = true;
          } else if (
            searchResult.essentialSection &&
            essentialsBookmarkIDs.includes(searchResult.essentialSection.id)
          ) {
            bookmarked = true;
          }

          searchResultCells.push(
            <SearchResultCell
              key={searchResult.type + recentSearchIndex}
              searchResult={searchResult}
              searchTerm={this.state.searchText}
              bookmarked={bookmarked}
              darkMode={this.props.screenProps.darkMode}
              debugMode={this.props.screenProps.debugMode}
              recentlyViewedResult={true}
              cellClicked={this.searchResultCellClicked.bind(this)}
            />
          );
        }
      }

      // recent search phrases
      for (
        let recentSearchPhrasesIndex = 0;
        recentSearchPhrasesIndex < this.state.recentSearchPhrases.length;
        recentSearchPhrasesIndex++
      ) {
        let recentSearchPhrase = this.state.recentSearchPhrases[recentSearchPhrasesIndex];

        searchResultPhraseCells.push(
          <div
            key={recentSearchPhrase}
            className="dashboardRecentSearchPhraseCell"
            onClick={this.recentSearchPhraseClicked.bind(this, recentSearchPhrase)}
          >
            {recentSearchPhrase}
          </div>
        );
      }
    } else {
      for (
        let matchingSearchResultIndex = 0;
        matchingSearchResultIndex < this.state.matchingSearchResults.length;
        matchingSearchResultIndex++
      ) {
        let matchingSearchResult = this.state.matchingSearchResults[matchingSearchResultIndex];

        let bookmarked = false;
        if (
          matchingSearchResult.clinicalNotice &&
          clinicalNoticeBookmarkIDs.includes(matchingSearchResult.clinicalNotice.ID)
        ) {
          bookmarked = true;
        } else if (matchingSearchResult.pathway && pathwayBookmarkIDs.includes(matchingSearchResult.pathway.id)) {
          bookmarked = true;
        } else if (matchingSearchResult.algorithm && algorithmBookmarkIDs.includes(matchingSearchResult.algorithm.id)) {
          bookmarked = true;
        } else if (
          matchingSearchResult.guidelineSummary &&
          guidelineBookmarkGLIDs.includes(matchingSearchResult.guidelineSummary.GLID)
        ) {
          bookmarked = true;
        } else if (
          matchingSearchResult.essentialSection &&
          essentialsBookmarkIDs.includes(matchingSearchResult.essentialSection.id)
        ) {
          bookmarked = true;
        }

        searchResultCells.push(
          <SearchResultCell
            key={matchingSearchResult.type + matchingSearchResultIndex}
            searchResult={matchingSearchResult}
            searchTerm={this.state.searchText}
            bookmarked={bookmarked}
            darkMode={this.props.screenProps.darkMode}
            debugMode={this.props.screenProps.debugMode}
            recentlyViewedResult={false}
            cellClicked={this.searchResultCellClicked.bind(this)}
          />
        );
      }
    }

    let searchText = "Search JRCALC PLUS";
    if (this.props.screenProps.app === "iCPG") {
      searchText = "Search iCPG";
    } else if (this.props.screenProps.app === "CFR") {
      searchText = "Search Responder Plus";
    }

    return (
      <IonModal
        isOpen={this.state.searchModalVisible}
        onDidDismiss={this.showSearch.bind(this, false)}
        mode="ios"
        animated={false}
        id="searchModal"
      >
        <div className="guidelineListSearchHeader dashboardSearch">
          <div className={`guidelinesSearchInputContainer`}>
            <img alt="" src={this.props.screenProps.darkMode ? iconSearchDark : iconSearch} className="icon-44" />
            <IonInput
              value={this.state.searchText}
              className="searchInputField"
              placeholder={searchText}
              onIonInput={(event) => this.searchTextChanged(event)}
              clearOnEdit={false}
              ref={this.searchTextField}
              onKeyDown={(event) => this.enterButtonClicked(event)}
            />
            {this.state.searchText ? (
              <IonButton className="searchClearButton" mode="ios" onClick={this.clearSearch.bind(this)}>
                <img alt="" src={iconClearSearch} className="icon-44" />
              </IonButton>
            ) : null}
          </div>
          <IonButton className="searchCancelButton" mode="ios" onClick={this.showSearch.bind(this, false)}>
            Cancel
          </IonButton>
        </div>

        <div className="dashboardSearchContainer">
          {this.state.searchText.length > 1 ? (
            <div className="dashboardSearchFilterContainer globalContentOnlyWideMargin">
              <FilterChip
                name="All"
                selected={this.state.selectedSearchFilter === "All"}
                darkMode={this.props.screenProps.darkMode}
                onClick={this.searchFilterChipClicked.bind(this, "All")}
              />
              <FilterChip
                name="Guideline"
                selected={this.state.selectedSearchFilter === "Guideline"}
                darkMode={this.props.screenProps.darkMode}
                onClick={this.searchFilterChipClicked.bind(this, "Guideline")}
              />
              {trustDisplayName !== "" ? (
                <FilterChip
                  name={trustDisplayName}
                  selected={this.state.selectedSearchFilter === trustDisplayName}
                  darkMode={this.props.screenProps.darkMode}
                  onClick={this.searchFilterChipClicked.bind(this, trustDisplayName)}
                />
              ) : null}
              <FilterChip
                name="Medicine"
                selected={this.state.selectedSearchFilter === "Medicine"}
                darkMode={this.props.screenProps.darkMode}
                onClick={this.searchFilterChipClicked.bind(this, "Medicine")}
              />
              <FilterChip
                name="Algorithm"
                selected={this.state.selectedSearchFilter === "Algorithm"}
                darkMode={this.props.screenProps.darkMode}
                onClick={this.searchFilterChipClicked.bind(this, "Algorithm")}
              />
              {this.props.screenProps.clinicalNotices.length > 0 ? (
                <FilterChip
                  name="Clinical Notice"
                  selected={this.state.selectedSearchFilter === "Clinical Notice"}
                  darkMode={this.props.screenProps.darkMode}
                  onClick={this.searchFilterChipClicked.bind(this, "Clinical Notice")}
                />
              ) : null}
              {allPathways.length > 0 ? (
                <FilterChip
                  name="Pathway"
                  selected={this.state.selectedSearchFilter === "Pathway"}
                  darkMode={this.props.screenProps.darkMode}
                  onClick={this.searchFilterChipClicked.bind(this, "Pathway")}
                />
              ) : null}
              {this.props.screenProps.essentialSections.length > 0 ? (
                <FilterChip
                  name="Essentials"
                  selected={this.state.selectedSearchFilter === "Essentials"}
                  darkMode={this.props.screenProps.darkMode}
                  onClick={this.searchFilterChipClicked.bind(this, "Essentials")}
                />
              ) : null}
              {this.props.screenProps.furtherResources.length > 0 ? (
                <FilterChip
                  name="Further Resource"
                  selected={this.state.selectedSearchFilter === "Further Resource"}
                  darkMode={this.props.screenProps.darkMode}
                  onClick={this.searchFilterChipClicked.bind(this, "Further Resource")}
                />
              ) : null}
            </div>
          ) : (
            <>
              {this.props.features.includes("trendingSearches") &&
              this.props.screenProps.trendingSearches.length > 0 ? (
                <div className="dashboardTrendingSearchesContainer">
                  <div className={`dashboardTrendingSearchesHeader ${DataController.getSectionClass(trust)}`}>
                    <div className="dashboardTrendingSearchesTitle">Trending searches</div>
                    <div className="dashboardTrendingSearchesDotSeparator" />
                    <div className="dashboardTrendingSearchesTrust">{trustDisplayName}</div>
                  </div>
                  <div className="dashboardTrendingSearchesPhraseContainer">
                    {this.props.screenProps.trendingSearches.map((trendingSearch, index) => {
                      return (
                        <div
                          key={trendingSearch}
                          className="dashboardTrendingSearchesPhrase"
                          onClick={this.recentSearchPhraseClicked.bind(this, trendingSearch)}
                        >
                          <img className="dashboardTrendingSearchesPhraseIcon" src={iconSearchGrey} alt="" />
                          {trendingSearch}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </>
          )}

          {filterInfoText !== "" ? (
            <div className="filterInfoContainer globalContentOnlyWideMargin">
              <div className="filterInfoText">{filterInfoText}</div>
            </div>
          ) : null}

          <div className="dashboardSearchResultsContainer">
            <div className="dashboardSearchResultsInnerContainer globalContentOnlyWideMargin">
              {showingRecentSearches && searchResultPhraseCells.length > 0 ? (
                <div>
                  <div>
                    <div className="dashboardRecentSearches">Recent searches</div>
                    <div className="dashboardClearRecentSearchesContainer">
                      <div
                        className="dashboardClearRecentSearchesText"
                        onClick={this.clearRecentSearchPhrases.bind(this)}
                      >
                        Clear
                      </div>
                    </div>
                  </div>
                  <div>{searchResultPhraseCells}</div>
                </div>
              ) : null}

              {showingRecentSearches && searchResultCells.length > 0 ? (
                <div className="dashboardRecentSearches">Recently viewed</div>
              ) : null}

              {searchResultCells}

              <div className="dashboardClearRecentSearchesContainer">
                {showingRecentSearches && searchResultCells.length > 0 ? (
                  <div className="dashboardClearRecentSearchesText" onClick={this.clearRecentSearches.bind(this)}>
                    Clear
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {this.state.searchText.length > 1 ? (
            <div className="globalSearchSuggestionContainer globalContentOnlyWideMargin">
              <p>Not found what you're looking for?</p>
              <IonButton
                mode="ios"
                className="filledButton globalSearchSuggestionButton"
                strong={true}
                onClick={() => {
                  this.props.screenProps.sendSupportEmail("Search Suggestion", this.state.searchText);
                }}
              >
                Contact us
              </IonButton>
            </div>
          ) : null}
        </div>

        <MessageBar messageBarText={this.props.screenProps.messageBarText} />
        <div className="appColorOverlay" />
      </IonModal>
    );
  }
}

export default GlobalSearchModal;
